import { Car } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";

/**
 * DrivingModeToggle — visible button + live status pill that sits on the
 * dashboard. When enabled, it shows the elapsed tracking time and current
 * speed (when the device exposes it). Off → muted; On → red pulse + light
 * background to make the active state unmistakable while glanced at on a
 * windscreen mount.
 */
function formatElapsed(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

export default function DrivingModeToggle({ enabled, onToggle, trackedSince, currentSpeedKph }) {
  const { t } = useTranslation();
  const [tick, setTick] = useState(Date.now());
  useEffect(() => {
    if (!enabled || !trackedSince) return;
    const id = setInterval(() => setTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [enabled, trackedSince]);

  const elapsed = enabled && trackedSince ? formatElapsed(tick - trackedSince) : null;
  const speedLabel = enabled && typeof currentSpeedKph === "number" && !isNaN(currentSpeedKph)
    ? `${Math.round(currentSpeedKph)} km/h`
    : null;

  return (
    <div className="flex items-center gap-2" data-testid="driving-mode-container">
      <Button
        variant="outline"
        size="sm"
        onClick={onToggle}
        data-testid="driving-mode-toggle"
        aria-pressed={enabled}
        className={
          enabled
            ? "rounded-sm border-red-300 bg-red-50 text-red-700 hover:bg-red-100"
            : "rounded-sm text-gray-700 hover:bg-gray-100"
        }
      >
        <Car className="w-4 h-4 mr-1.5" />
        <span className="text-xs font-bold uppercase tracking-widest">
          {enabled ? t("driving.on") : t("driving.off")}
        </span>
        {enabled && (
          <span className="ml-2 inline-flex items-center" aria-hidden>
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
            </span>
          </span>
        )}
      </Button>
      {enabled && (elapsed || speedLabel) && (
        <span
          className="text-[10px] font-mono text-gray-500 leading-tight"
          data-testid="driving-mode-status"
        >
          {elapsed && <span>{elapsed}</span>}
          {elapsed && speedLabel && <span className="mx-1">·</span>}
          {speedLabel && <span>{speedLabel}</span>}
        </span>
      )}
    </div>
  );
}
